import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Table, Tag, Button, Switch, Space, message as antMessage } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../../services/api';

const getInvoicePaymentStatusLabel = (status) => {
  const s = String(status || '').toUpperCase();
  if (s === 'PAID') return 'Baixa Manual';
  if (s === 'APPROVED') return 'Pago';
  if (s === 'CANCELED') return 'Cancelado';
  if (s === 'PENDING') return 'Pendente';
  return 'Desconhecido';
};

const statusColor = (s) => {
  const n = String(s || '').toUpperCase();
  if (n === 'PAID' || n === 'APPROVED') return 'green';
  if (n === 'PENDING') return 'orange';
  if (n === 'CANCELED') return 'red';
  return 'default';
};

const fmtDate = (v) => v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '';
const isMobile = window.innerWidth <= 768;

const InvoicesModal = ({ open, chargeId, onClose }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [paidOrApproved, setPaidOrApproved] = useState(true);
  const [canceled, setCanceled] = useState(false);

  const load = useCallback(async () => {
    if (!chargeId) return;
    setLoading(true);
    try {
      const { data } = await api.get('/organizer-charges/invoices', {
        params: {
          charge_id: chargeId,
          page,
          limit,
          paidOrApproved: paidOrApproved ? '1' : '0',
          canceled: canceled ? '1' : '0',
        },
      });
      setItems(data.invoices || []);
      setTotal(data.total || 0);
    } catch {
      antMessage.error('Erro ao carregar faturas');
    } finally {
      setLoading(false);
    }
  }, [chargeId, page, limit, paidOrApproved, canceled]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // reset page when filters change
  useEffect(() => { setPage(1); }, [paidOrApproved, canceled, chargeId]);

  const handleDownload = (record) => {
    Modal.confirm({
      title: 'Confirmar Baixa de Pagamento',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p><strong>Jogador:</strong> {record.player_name}</p>
          <p><strong>Telefone:</strong> {record.player_phone}</p>
          <p><strong>Valor:</strong> R$ {parseFloat(record.charge_amount || 0).toFixed(2)}</p>
          <br />
          <p>Tem certeza que deseja baixar esta fatura?</p>
        </div>
      ),
      okText: 'Sim, baixar pagamento',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await api.put(`/organizer-charges/invoices/${record.id}/download`);
          antMessage.success('Fatura baixada com sucesso');
          load();
        } catch {
          antMessage.error('Erro ao baixar fatura');
        }
      },
    });
  };

  const columns = [
    { title: 'Jogador', dataIndex: 'player_name', width: 160 },
    { title: 'Telefone', dataIndex: 'player_phone', width: 130 },
    {
      title: 'Valor',
      dataIndex: 'charge_amount',
      width: 100,
      align: 'right',
      render: (v) => `R$ ${parseFloat(v || 0).toFixed(2)}`,
    },
    {
      title: 'Status',
      dataIndex: 'payment_status',
      width: 120,
      render: (s) => <Tag color={statusColor(s)}>{getInvoicePaymentStatusLabel(s)}</Tag>,
    },
    { title: 'Emitida em', dataIndex: 'created_at', width: 140, render: fmtDate },
    { title: 'Atualizada em', dataIndex: 'updated_at', width: 140, render: fmtDate },
    { title: 'Paga/Baixada em', dataIndex: 'payment_at', width: 140, render: fmtDate },
    {
      title: 'Ações',
      width: 110,
      render: (_, r) =>
        String(r.payment_status || '').toUpperCase() === 'PENDING' ? (
          <Button type="primary" size="small" onClick={() => handleDownload(r)}>
            Baixar
          </Button>
        ) : null,
    },
  ];

  return (
    <Modal
      styles={
        isMobile
          ? {
              wrapper: { paddingInline: 0 },
              content: { padding: '12px 0px 12px 0px' }
            }
          : { content: { padding: 24 } }
      }
      title={
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'center' : 'center',
            justifyContent: isMobile ? 'center' : 'space-between',
            gap: isMobile ? 0 : 16,
            boxSizing: 'border-box',
            padding: isMobile ? '0 8px' : 0
          }}
        >
          <div
            style={{
              width: isMobile ? '100%' : 'auto',
              flex: isMobile ? 'unset' : 1,
              textAlign: 'left',
              alignSelf: 'center'
            }}
          >
            Faturas da Cobrança
          </div>
          <div style={{ fontWeight: 'normal', fontSize: '0.9rem', marginTop: isMobile ? '0.8rem' : 0, marginBottom: isMobile ? '0.6rem' : 0, marginLeft: isMobile ? 0 : 'auto', marginRight: isMobile ? 0 : 36 }}>
            <div
              style={{
                display: 'inline-flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 8,
                border: '1px solid #e8e8e8',
                borderRadius: 12,
                background: '#fafafa',
                padding: isMobile ? '10px 10px' : '8px 12px',
                maxWidth: '100%',
                width: isMobile ? '100%' : 'auto',
                boxSizing: 'border-box'
              }}
            >
              <span style={{ fontWeight: 600, fontSize: '0.82rem', letterSpacing: '0.2px' }}>Mostrar</span>

              <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <Space
                  size={12}
                  wrap={false}
                  style={{ display: 'inline-flex', whiteSpace: 'nowrap' }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <span>Pagas/aprovadas</span>
                    <Switch
                      key='switch-paid-invoices'
                      defaultChecked
                      style={{ marginLeft: 6 }}
                      checked={paidOrApproved}
                      onChange={setPaidOrApproved}
                    />
                  </span>

                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <span>Canceladas</span>
                    <Switch
                      key='switch-canceled-invoices'
                      style={{ marginLeft: 6 }}
                      checked={canceled}
                      onChange={setCanceled}
                    />
                  </span>
                </Space>
              </div>
            </div>
          </div>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={isMobile ? '100%' : 1100}
      style={isMobile ? { top: 20, margin: 0, maxWidth: 'none' } : {}}
      destroyOnClose
    >
      <Table
        rowKey="id"
        loading={loading}
        dataSource={items}
        columns={columns}
        pagination={{
          current: page,
          pageSize: limit,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          onChange: (p, ps) => { setPage(p); setLimit(ps); },
        }}
        size="small"
        scroll={{ x: 'max-content' }}
      />
    </Modal>
  );
};

export default InvoicesModal;
